import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { SEO } from '@/components/SEO';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Shield, Zap, Lock, TrendingUp, FileCheck, Database, Bell, ArrowRight, CheckCircle2, RefreshCw } from 'lucide-react';
import logo from '@/assets/logo.png';
import { generateBreadcrumbSchema } from '@/lib/structuredData';

export default function Features() {
  const navigate = useNavigate();

  const features = [
    {
      icon: RefreshCw,
      title: "Automatic Reconciliation",
      description: "Advanced algorithms extract data from invoices with 98%+ accuracy, detecting duplicates and anomalies automatically.",
      benefits: ["Automatic data extraction", "Duplicate detection", "Fraud pattern recognition", "Smart vendor matching"]
    },
    {
      icon: Zap,
      title: "Instant Processing",
      description: "Match thousands of transactions in seconds with advanced matching algorithms and fuzzy logic.",
      benefits: ["<2 minute processing", "Batch reconciliation", "Smart suggestions", "One-click matching"]
    },
    {
      icon: Database,
      title: "Tally ERP Integration",
      description: "Seamlessly sync with Tally ERP via XML imports and real-time data synchronization.",
      benefits: ["XML import support", "Bidirectional sync", "Automatic voucher creation", "Real-time updates"]
    },
    {
      icon: Shield,
      title: "Real-Time Fraud Detection",
      description: "Continuous monitoring of suspicious patterns, duplicate payments, and unauthorized transactions.",
      benefits: ["Real-time alerts", "Risk scoring", "Pattern analysis", "Compliance checks"]
    },
    {
      icon: TrendingUp,
      title: "Cash Flow Forecasting",
      description: "Precision predictions help you anticipate future cash positions and make informed decisions.",
      benefits: ["30-day forecasts", "Trend analysis", "Scenario modeling", "Custom alerts"]
    },
    {
      icon: FileCheck,
      title: "Automated Reporting",
      description: "Generate comprehensive financial reports instantly with customizable templates and export options.",
      benefits: ["One-click reports", "Custom templates", "PDF/Excel export", "Scheduled delivery"]
    },
    {
      icon: Lock,
      title: "Bank-Level Security",
      description: "AES-256 encryption, row-level security policies, and SOC 2 compliance protect your data.",
      benefits: ["End-to-end encryption", "Role-based access", "Audit trails", "Data isolation"]
    },
    {
      icon: Bell,
      title: "Proactive Notifications",
      description: "Get intelligent alerts for flagged invoices, reconciliation matches, and critical events.",
      benefits: ["Push notifications", "Email alerts", "Custom triggers", "Priority levels"]
    }
  ];

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: 'https://getsentinel.pro/' },
    { name: 'Features', url: 'https://getsentinel.pro/features' }
  ]);

  return (
    <>
      <SEO
        title="Features - Financial Reconciliation | Sentinel"
        description="Explore Sentinel's comprehensive features: intelligent invoice analysis, instant reconciliation, Tally integration, fraud detection, and advanced reporting tools."
      />
      
      {/* Breadcrumb Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      
      <div className="min-h-screen bg-background">
        {/* Navigation */}
        <nav className="border-b border-border/50 bg-background/95 backdrop-blur-xl sticky top-0 z-50">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
                <img src={logo} alt="SENTINEL Logo" className="w-10 h-10 brightness-0 dark:brightness-100 dark:invert" />
                <span className="text-2xl font-bold">SENTINEL</span>
              </div>
              <div className="flex items-center gap-4">
                <ThemeToggle />
                <Button onClick={() => navigate('/auth')} className="rounded-2xl">
                  Sign In
                </Button>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-hero" />
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 pb-2">
                Powerful Features for
                <span className="bg-gradient-primary bg-clip-text text-transparent"> Modern Finance</span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Everything you need to automate financial reconciliation, detect fraud, and gain actionable insights
              </p>
              <Button size="lg" onClick={() => navigate('/auth')} className="shadow-premium rounded-2xl">
                Start Free Trial <ArrowRight className="ml-2" size={20} />
              </Button>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <Card key={index} className="p-8 hover:shadow-card-hover transition-all duration-300 border-border/50 rounded-3xl">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-primary flex items-center justify-center mb-6 shadow-glow">
                    <feature.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">{feature.title}</h3>
                  <p className="text-muted-foreground mb-6 leading-relaxed">{feature.description}</p>
                  <ul className="space-y-3">
                    {feature.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-primary-dark dark:text-primary flex-shrink-0" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-primary relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-10" />
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 pb-1 text-white">
                Ready to Transform Your Finance Operations?
              </h2>
              <p className="text-xl text-white/90 mb-8">
                Join hundreds of businesses saving time and money with Sentinel
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="secondary" onClick={() => navigate('/auth')} className="shadow-card rounded-2xl">
                  Start Free Trial
                </Button>
                <Button size="lg" variant="outline" onClick={() => navigate('/pricing')} className="text-white border-white hover:bg-white/10 rounded-2xl">
                  View Pricing
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-border/50 py-8 bg-surface/30">
          <div className="container mx-auto px-6 text-center text-sm text-muted-foreground">
            <p>&copy; 2025 SENTINEL. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </>
  );
}
